<template>
  <section class="mission-vision-values">
    <div class="content-container">
      <!-- Section Header -->
      <div class="mv-header">
        <h2 class="mv-section-title">{{ headerData.title }} <span class="highlight">{{ headerData.highlight }}</span></h2>
        <p class="mv-section-subtitle">{{ headerData.subtitle }}</p>
      </div>
      
      <div class="mv-container">
        <div 
          v-for="(card, index) in coreValuesCards" 
          :key="index"
          class="mv-card wow fadeInUp" 
          :class="`${card.type}-card`"
          :data-wow-delay="`${(index + 1) * 0.1}s`"
        >
          <div :class="`mv-ring ${card.type}-ring`">
            <div class="ring-segment segment-1"></div>
            <div class="ring-segment segment-2"></div>
            <div class="ring-segment segment-3"></div>
            <div class="ring-segment segment-4"></div>
          </div>
          <div class="mv-content">
            <div class="mv-icon">
              <i :class="card.icon"></i>
            </div>
            <h3 class="mv-title">{{ card.title }}</h3>
            <p class="mv-description">{{ card.description }}</p>
          </div>
          <div class="mv-reflection"></div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "WhySogdoItsCoreValuesComp",
  props: {
    headerData: {
      type: Object,
      required: true,
      default: () => ({
        title: "Our",
        highlight: "Core Values",
        subtitle: "The principles that guide everything we do at Sogdo"
      })
    },
    coreValuesCards: {
      type: Array,
      required: true,
      default: () => []
    }
  }
};
</script>

<style scoped>
/* Import Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap');

/* Import Flaticon */
@import '@/assets/scss/Flaticon.scss';

/* Mission, Vision, Values Section */
.mission-vision-values {
  padding: 100px 0;
  background: linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%);
  position: relative;
  overflow: hidden;
}

/* Section Header */
.mv-header {
  text-align: center;
  margin-bottom: 80px;
}

.mv-section-title {
  font-size: 48px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
}

.mv-section-title .highlight {
  background: linear-gradient(135deg, #F6EC4F, #FB0E76);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.mv-section-subtitle {
  font-size: 20px;
  color: #ccc;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.mv-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 30px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
}

.mv-card {
  position: relative;
  width: 350px;
  height: 350px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transform: translateY(50px);
  animation: slideInUp 0.8s ease-out forwards;
}

.mv-card:nth-child(1) { animation-delay: 0.1s; }
.mv-card:nth-child(2) { animation-delay: 0.2s; }
.mv-card:nth-child(3) { animation-delay: 0.3s; }

/* Circular Ring Design */
.mv-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  z-index: 1;
  transform: rotate(45deg);
  opacity: 0.7;
}

.ring-segment {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 8px solid transparent;
}

/* Mission Ring - Orange/Red */
.mission-ring .segment-1 {
  border-top-color: #FF6B35;
  transform: rotate(0deg);
}

.mission-ring .segment-2 {
  border-right-color: #FF6B35;
  transform: rotate(90deg);
}

.mission-ring .segment-3 {
  border-bottom-color: #FF6B35;
  transform: rotate(180deg);
}

.mission-ring .segment-4 {
  border-left-color: #FF6B35;
  transform: rotate(270deg);
}

/* Vision Ring - Cyan */
.vision-ring .segment-1 {
  border-top-color: #00D4FF;
  transform: rotate(0deg);
}

.vision-ring .segment-2 {
  border-right-color: #00D4FF;
  transform: rotate(90deg);
}

.vision-ring .segment-3 {
  border-bottom-color: #00D4FF;
  transform: rotate(180deg);
}

.vision-ring .segment-4 {
  border-left-color: #00D4FF;
  transform: rotate(270deg);
}

/* Values Ring - Yellow */
.values-ring .segment-1 {
  border-top-color: #FFD700;
  transform: rotate(0deg);
}

.values-ring .segment-2 {
  border-right-color: #FFD700;
  transform: rotate(90deg);
}

.values-ring .segment-3 {
  border-bottom-color: #FFD700;
  transform: rotate(180deg);
}

.values-ring .segment-4 {
  border-left-color: #FFD700;
  transform: rotate(270deg);
}

/* Card Content */
.mv-content {
  position: relative;
  z-index: 2;
  background: #fff;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.mv-icon {
  width: 70px;
  height: 70px;
  margin-bottom: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  color: #333;
}

.mv-title {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin-bottom: 18px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.mv-description {
  font-size: 16px;
  color: #222;
  line-height: 1.5;
  text-align: center;
}

/* Reflection Effect */
.mv-reflection {
  position: absolute;
  bottom: -100px;
  left: 50%;
  transform: translateX(-50%) scaleY(-1);
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 50%, transparent 100%);
  z-index: 0;
  opacity: 0.2;
}

/* Animation */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .mv-section-title {
    font-size: 36px;
  }
  
  .mv-section-subtitle {
    font-size: 16px;
  }
  
  .mv-container {
    flex-direction: column;
    gap: 25px;
  }
  
  .mv-card {
    width: 320px;
    height: 320px;
  }
  
  .mv-content {
    width: 240px;
    height: 240px;
    padding: 25px;
  }
  
  .mv-icon {
    width: 60px;
    height: 60px;
    font-size: 30px;
    margin-bottom: 20px;
  }
  
  .mv-title {
    font-size: 20px;
    margin-bottom: 15px;
  }
  
  .mv-description {
    font-size: 16px;
  }
  
  .mv-reflection {
    width: 240px;
    height: 240px;
    bottom: -80px;
  }
}

@media (max-width: 480px) {
  .mv-section-title {
    font-size: 28px;
  }
  
  .mv-section-subtitle {
    font-size: 14px;
  }
  
  .mv-container {
    gap: 20px;
  }
  
  .mv-card {
    width: 280px;
    height: 280px;
  }
  
  .mv-content {
    width: 200px;
    height: 200px;
    padding: 20px;
  }
  
  .mv-icon {
    width: 50px;
    height: 50px;
    font-size: 26px;
    margin-bottom: 15px;
  }
  
  .mv-title {
    font-size: 18px;
    margin-bottom: 12px;
  }
  
  .mv-description {
    font-size: 16px;
  }
  
  .mv-reflection {
    width: 200px;
    height: 200px;
    bottom: -70px;
  }
}
</style>
